// Instance Expander for infragraph
// Expands count-based instances into individual named instances

import { Instance, ExpandedInstance, Device, ExpandedComponent, LayoutCoordinates } from '../types/infragraph';

/**
 * Calculate coordinates for a specific index from layout configuration
 * Handles three modes: coordinate lists, spacing, and single coordinate
 * Returns user coordinates (not yet transformed to SVG space)
 *
 * Shared utility function used by both instance and component expansion
 */
export function calculateLayoutCoordinates(
  layout: LayoutCoordinates | undefined,
  index: number,
  totalCount: number
): { x: number | undefined; y: number | undefined } {
  if (!layout) {
    return { x: undefined, y: undefined };
  }

  let coordX: number | undefined;
  let coordY: number | undefined;

  // Mode 1: Coordinate lists
  if (layout.x_coordinates || layout.y_coordinates) {
    const xCoords = layout.x_coordinates;
    const yCoords = layout.y_coordinates;

    if (xCoords && xCoords.length > 0) {
      const xIndex = Math.min(index, xCoords.length - 1);
      coordX = xCoords[xIndex];
    } else {
      coordX = layout.x ?? 0;
    }

    if (yCoords && yCoords.length > 0) {
      const yIndex = Math.min(index, yCoords.length - 1);
      coordY = yCoords[yIndex];
    } else {
      coordY = layout.y ?? 0;
    }
  }
  // Mode 2: Spacing from center point
  else if (layout.x_spacing !== undefined || layout.y_spacing !== undefined) {
    const centerX = layout.x ?? 0;
    const centerY = layout.y ?? 0;
    const xSpacing = layout.x_spacing ?? 0;
    const ySpacing = layout.y_spacing ?? 0;
    const halfCount = (totalCount - 1) / 2;
    const offset = index - halfCount;

    coordX = Math.round(centerX + offset * xSpacing);
    coordY = Math.round(centerY + offset * ySpacing);
  }
  // Mode 3: Single coordinate - all share same position
  else if (layout.x !== undefined && layout.y !== undefined) {
    coordX = layout.x;
    coordY = layout.y;
  }

  return { x: coordX, y: coordY };
}

export class InstanceExpander {
  /**
   * Expand a single instance based on its count field
   *
   * @param instance - The instance to expand
   * @returns Array of expanded instances
   *
   * @example
   * expandInstance({name: "server", device: "GPU_SERVER", count: 3})
   * → [
   *     {fullName: "server.0", baseName: "server", index: 0, device: "GPU_SERVER"},
   *     {fullName: "server.1", baseName: "server", index: 1, device: "GPU_SERVER"},
   *     {fullName: "server.2", baseName: "server", index: 2, device: "GPU_SERVER"}
   *   ]
   */
  expandInstance(instance: Instance): ExpandedInstance[] {
    const expanded: ExpandedInstance[] = [];

    // If count is 1, don't add index suffix - use the name as-is
    if (instance.count === 1) {
      // Add fixed: true to layout if it has coordinates
      let layout = instance.layout;
      if (layout && layout.x !== undefined && layout.y !== undefined) {
        layout = {
          ...layout,
          fixed: layout.fixed ?? true
        };
      }

      expanded.push({
        fullName: instance.name,
        baseName: instance.name,
        index: 0,
        device: instance.device,
        description: instance.description,
        location: instance.location,
        layout
      });
    } else {
      // For count > 1, handle layout coordinates in three modes:
      // 1. coordinate lists: Separate x and y lists (can have different lengths, last repeats)
      // 2. spacing mode: Spread instances from center using x_spacing/y_spacing
      // 3. single coordinate: All instances get the same coordinate
      for (let i = 0; i < instance.count; i++) {
        let instanceLayout = instance.layout;

        if (instance.layout) {
          // Mode 1: Separate coordinate lists for x and y
          if (instance.layout.x_coordinates || instance.layout.y_coordinates) {
            const xCoords = instance.layout.x_coordinates;
            const yCoords = instance.layout.y_coordinates;

            // Get x coordinate (from list or fallback to single x or 0)
            let xValue: number;
            if (xCoords && xCoords.length > 0) {
              const xIndex = Math.min(i, xCoords.length - 1);
              xValue = xCoords[xIndex];
            } else {
              xValue = instance.layout.x ?? 0;
            }

            // Get y coordinate (from list or fallback to single y or 0)
            let yValue: number;
            if (yCoords && yCoords.length > 0) {
              const yIndex = Math.min(i, yCoords.length - 1);
              yValue = yCoords[yIndex];
            } else {
              yValue = instance.layout.y ?? 0;
            }

            instanceLayout = {
              x: xValue,
              y: yValue,
              fixed: instance.layout.fixed ?? true
            };
          }
          // Mode 2: Spacing from center point
          else if (instance.layout.x_spacing !== undefined || instance.layout.y_spacing !== undefined) {
            const centerX = instance.layout.x ?? 0;
            const centerY = instance.layout.y ?? 0;
            const xSpacing = instance.layout.x_spacing ?? 0;
            const ySpacing = instance.layout.y_spacing ?? 0;

            // Calculate offset from center for this instance
            // For even count, instances are centered around the origin
            // For odd count, one instance is at the center
            const halfCount = (instance.count - 1) / 2;
            const offset = i - halfCount;

            instanceLayout = {
              x: Math.round(centerX + offset * xSpacing),
              y: Math.round(centerY + offset * ySpacing),
              fixed: instance.layout.fixed ?? true
            };
          }
          // Mode 3: Single coordinate - all instances share the same layout
          else if (instance.layout.x !== undefined && instance.layout.y !== undefined) {
            instanceLayout = {
              x: instance.layout.x,
              y: instance.layout.y,
              fixed: instance.layout.fixed ?? true
            };
          }
          // If only x or only y is provided (not both), keep original layout which may be partial
        }

        expanded.push({
          fullName: `${instance.name}.${i}`,
          baseName: instance.name,
          index: i,
          device: instance.device,
          description: instance.description,
          location: instance.location,
          layout: instanceLayout
        });
      }
    }

    return expanded;
  }

  /**
   * Expand all instances in an array
   *
   * @param instances - Array of instances to expand
   * @returns Array of all expanded instances
   */
  expandAllInstances(instances: Instance[]): ExpandedInstance[] {
    return instances.flatMap(instance => this.expandInstance(instance));
  }

  /**
   * Expand components within a device based on count
   *
   * @param device - The device containing components
   * @param instanceName - The specific instance name (e.g., "server.0")
   * @returns Array of expanded components
   *
   * @example
   * expandDeviceComponents(gpuServerDevice, "server.0")
   * → [
   *     {fullName: "server.0.gpu.0", instanceName: "server.0", componentBaseName: "gpu", index: 0, ...},
   *     {fullName: "server.0.gpu.1", instanceName: "server.0", componentBaseName: "gpu", index: 1, ...},
   *     ...
   *   ]
   */
  expandDeviceComponents(device: Device, instanceName: string): ExpandedComponent[] {
    const expanded: ExpandedComponent[] = [];

    for (const component of device.components) {
      for (let i = 0; i < component.count; i++) {
        expanded.push({
          fullName: `${instanceName}.${component.name}.${i}`,
          instanceName: instanceName,
          componentBaseName: component.name,
          index: i,
          component: component
        });
      }
    }

    return expanded;
  }

  /**
   * Expand all components for all instances of a device
   *
   * @param device - The device containing components
   * @param instances - Array of expanded instances using this device
   * @returns Array of all expanded components
   */
  expandAllDeviceComponents(device: Device, instances: ExpandedInstance[]): ExpandedComponent[] {
    return instances.flatMap(instance =>
      this.expandDeviceComponents(device, instance.fullName)
    );
  }

  /**
   * Create a map of instance names to their expanded instances
   *
   * @param instances - Array of instances
   * @returns Map of base name to expanded instances
   *
   * @example
   * createInstanceMap([{name: "server", count: 2, device: "GPU"}])
   * → Map { "server" → [{fullName: "server.0", ...}, {fullName: "server.1", ...}] }
   */
  createInstanceMap(instances: Instance[]): Map<string, ExpandedInstance[]> {
    const map = new Map<string, ExpandedInstance[]>();

    for (const instance of instances) {
      const expanded = this.expandInstance(instance);
      map.set(instance.name, expanded);
    }

    return map;
  }

  /**
   * Get total count of all instances
   *
   * @param instances - Array of instances
   * @returns Total number of expanded instances
   */
  getTotalInstanceCount(instances: Instance[]): number {
    return instances.reduce((sum, instance) => sum + instance.count, 0);
  }

  /**
   * Find an expanded instance by its full name
   *
   * @param fullName - The full instance name (e.g., "server.0")
   * @param expandedInstances - Array of expanded instances
   * @returns The matching expanded instance or undefined
   */
  findInstanceByFullName(
    fullName: string,
    expandedInstances: ExpandedInstance[]
  ): ExpandedInstance | undefined {
    return expandedInstances.find(instance => instance.fullName === fullName);
  }

  /**
   * Parse a full instance name into its parts
   *
   * @param fullName - The full instance name (e.g., "server.0")
   * @returns Object with baseName and index
   *
   * @example
   * parseInstanceName("server.0") → {baseName: "server", index: 0}
   */
  parseInstanceName(fullName: string): { baseName: string; index: number } {
    const lastDot = fullName.lastIndexOf('.');
    if (lastDot === -1) {
      throw new Error(`Invalid instance name format: ${fullName}`);
    }

    const baseName = fullName.substring(0, lastDot);
    const indexStr = fullName.substring(lastDot + 1);
    const index = parseInt(indexStr, 10);

    if (isNaN(index)) {
      throw new Error(`Invalid index in instance name: ${fullName}`);
    }

    return { baseName, index };
  }

  /**
   * Check if a string is a valid instance name
   *
   * @param name - The name to validate
   * @returns true if valid, false otherwise
   */
  isValidInstanceName(name: string): boolean {
    try {
      this.parseInstanceName(name);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Group expanded instances by their device type
   *
   * @param expandedInstances - Array of expanded instances
   * @returns Map of device type to instances
   */
  groupByDevice(expandedInstances: ExpandedInstance[]): Map<string, ExpandedInstance[]> {
    const map = new Map<string, ExpandedInstance[]>();

    for (const instance of expandedInstances) {
      const existing = map.get(instance.device) || [];
      existing.push(instance);
      map.set(instance.device, existing);
    }

    return map;
  }

  /**
   * Filter expanded instances by device type
   *
   * @param expandedInstances - Array of expanded instances
   * @param deviceType - The device type to filter by
   * @returns Filtered array of instances
   */
  filterByDevice(expandedInstances: ExpandedInstance[], deviceType: string): ExpandedInstance[] {
    return expandedInstances.filter(instance => instance.device === deviceType);
  }
}

// Export singleton instance for convenience
export const instanceExpander = new InstanceExpander();

// Export helper functions for direct use
export function expandInstance(instance: Instance): ExpandedInstance[] {
  return instanceExpander.expandInstance(instance);
}

export function expandAllInstances(instances: Instance[]): ExpandedInstance[] {
  return instanceExpander.expandAllInstances(instances);
}

export function expandDeviceComponents(device: Device, instanceName: string): ExpandedComponent[] {
  return instanceExpander.expandDeviceComponents(device, instanceName);
}

export function parseInstanceName(fullName: string): { baseName: string; index: number } {
  return instanceExpander.parseInstanceName(fullName);
}
